import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, Alert, Spinner } from "react-bootstrap";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MdDragIndicator, MdDelete } from "react-icons/md";
import { TbTags } from "react-icons/tb";
import Title from "../common/Title";
import { runbookTagService } from "../../services/runbookTag.service";

const DEFAULT_COLOR = "#6c757d";

function SortableTagRow({ tag, onRename, onRecolor, onToggleDefaultCollapsed, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tag.tagId,
  });
  const [name, setName] = useState(tag.name);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => setName(tag.name), [tag.name]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="d-flex align-items-center gap-2 py-2 border-bottom">
      <span {...attributes} {...listeners} className="text-muted" style={{ cursor: "grab" }}>
        <MdDragIndicator size={20} />
      </span>
      <Form.Control
        type="color"
        value={tag.color || DEFAULT_COLOR}
        onChange={(e) => onRecolor(tag.tagId, e.target.value)}
        title="Tag color"
        style={{ width: "2.5rem", height: "2.5rem", padding: "2px", flexShrink: 0 }}
      />
      <Form.Control
        size="sm"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => {
          if (name.trim() && name !== tag.name) onRename(tag.tagId, name.trim());
        }}
      />
      <Form.Check
        type="switch"
        id={`default-collapsed-${tag.tagId}`}
        label="Collapsed by default"
        className="text-nowrap flex-shrink-0"
        checked={!!tag.defaultCollapsed}
        onChange={(e) => onToggleDefaultCollapsed(tag.tagId, e.target.checked)}
      />
      {confirmingDelete ? (
        <div className="d-flex gap-1 flex-shrink-0">
          <Button variant="danger" size="sm" onClick={() => onDelete(tag.tagId)}>
            Delete
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={() => setConfirmingDelete(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <Button
          variant="outline-danger"
          size="sm"
          className="flex-shrink-0"
          onClick={() => setConfirmingDelete(true)}
          title="Delete tag"
        >
          <MdDelete />
        </Button>
      )}
    </div>
  );
}

// #2651: create/rename/recolor/delete/reorder a client's runbook groups (tags), plus each group's
// "collapsed by default" state -- promoted out of the old Manage Tags modal on the runbook edit
// forms into its own Administration page, matching Users/Permissions/Runbook Systems. Mutates
// directly via the service (no Redux), same short-lived pattern as EditPicklistValuesModal. Each
// action persists immediately rather than staging changes behind one big Save button, since
// there's no single coherent "draft" state across create/rename/recolor/reorder/delete.
function RunbookTagGrid({ user, hideTitle }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [newTagName, setNewTagName] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const refresh = useCallback(() => {
    if (!user?.clientId) return;
    setLoading(true);
    runbookTagService.get({ clientId: user.clientId }).then(
      (response) => {
        setTags(response.data || []);
        setLoading(false);
      },
      () => {
        setError(true);
        setLoading(false);
      }
    );
  }, [user?.clientId]);

  useEffect(() => {
    setError(false);
    refresh();
  }, [refresh]);

  const handleCreate = () => {
    if (!newTagName.trim() || !user?.clientId) return;
    runbookTagService.create({ clientId: user.clientId, name: newTagName.trim() }).then(() => {
      setNewTagName("");
      refresh();
    }, () => setError(true));
  };

  const handleRename = (tagId, name) => {
    const tag = tags.find((t) => t.tagId === tagId);
    runbookTagService
      .update({ tagId, name, color: tag?.color, defaultCollapsed: !!tag?.defaultCollapsed })
      .then(refresh, () => setError(true));
  };

  const handleRecolor = (tagId, color) => {
    // Optimistic local update so the swatch feels instant while the request is in flight.
    setTags((prev) => prev.map((t) => (t.tagId === tagId ? { ...t, color } : t)));
    const tag = tags.find((t) => t.tagId === tagId);
    runbookTagService
      .update({ tagId, name: tag?.name, color, defaultCollapsed: !!tag?.defaultCollapsed })
      .then(refresh, () => setError(true));
  };

  const handleToggleDefaultCollapsed = (tagId, defaultCollapsed) => {
    // Optimistic local update, same reasoning as recolor above.
    setTags((prev) => prev.map((t) => (t.tagId === tagId ? { ...t, defaultCollapsed } : t)));
    const tag = tags.find((t) => t.tagId === tagId);
    runbookTagService
      .update({ tagId, name: tag?.name, color: tag?.color, defaultCollapsed })
      .then(refresh, () => setError(true));
  };

  const handleDelete = (tagId) => {
    runbookTagService.remove({ tagId }).then(refresh, () => setError(true));
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = tags.findIndex((t) => t.tagId === active.id);
    const newIndex = tags.findIndex((t) => t.tagId === over.id);
    const reordered = arrayMove(tags, oldIndex, newIndex);
    setTags(reordered);

    runbookTagService
      .reorder({ clientId: user.clientId, tagIds: reordered.map((t) => t.tagId) })
      .then(refresh, () => setError(true));
  };

  return (
    <div className="p-3">
      {!hideTitle && <Title text="Runbook Groups" icon={<TbTags className="mb-1" />} />}

      {error && <Alert variant="danger">Something went wrong. Please try again.</Alert>}
      <div className="small text-muted mb-2">
        Deleting a group ungroups any runbooks using it -- it does not delete the runbooks.
        "Collapsed by default" controls how a group first appears for anyone who hasn't manually
        expanded or collapsed it themselves.
      </div>
      {loading ? (
        <div className="text-center p-3">
          <Spinner animation="border" size="sm" />
        </div>
      ) : tags.length === 0 ? (
        <div className="text-muted small mb-3">No runbook groups yet -- add one below.</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={tags.map((t) => t.tagId)} strategy={verticalListSortingStrategy}>
            {tags.map((tag) => (
              <SortableTagRow
                key={tag.tagId}
                tag={tag}
                onRename={handleRename}
                onRecolor={handleRecolor}
                onToggleDefaultCollapsed={handleToggleDefaultCollapsed}
                onDelete={handleDelete}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}

      <div className="d-flex gap-2 mt-3">
        <Form.Control
          size="sm"
          placeholder="New group name"
          value={newTagName}
          maxLength={50}
          onChange={(e) => setNewTagName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleCreate();
          }}
        />
        <Button size="sm" onClick={handleCreate} disabled={!newTagName.trim()}>
          Add Group
        </Button>
      </div>
    </div>
  );
}

export default RunbookTagGrid;
